import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, CalendarClock, Trophy, MessagesSquare, LineChart, Users2 } from "lucide-react";
import Logo from "../components/Logo";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/5b3f7fe2-851e-461f-95bd-10cd49290461/images/237599b366f4de622add06ebc397b2a33011e4e3e25ab26c08eac26361f3dab9.png";
const DASHBOARD_IMG = "https://static.prod-images.emergentagent.com/jobs/5b3f7fe2-851e-461f-95bd-10cd49290461/images/55f74d4da07d1267831e1996d71e6de5ec52d63ca1d68a334a71e7d1a703c01e.png";
const ABSTRACT = "https://static.prod-images.emergentagent.com/jobs/5b3f7fe2-851e-461f-95bd-10cd49290461/images/9c0ddd334756b2b54f7ffc02f002eafd17352acd9f339582e3d319e9bb511d97.png";
const ACTION = [
  "https://images.unsplash.com/photo-1756477558468-b3e485757470?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  "https://images.unsplash.com/photo-1761644695629-7227bba59fc4?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85",
  "https://images.pexels.com/photos/35688546/pexels-photo-35688546.jpeg?auto=compress&cs=tinysrgb&w=940",
  "https://images.pexels.com/photos/32975182/pexels-photo-32975182.jpeg?auto=compress&cs=tinysrgb&w=940",
];

function Header() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
        <Logo className="h-14 md:h-16 w-auto" testId="landing-logo" />
        <nav className="hidden md:flex items-center gap-8 text-sm text-[#A3A3A3]">
          <a href="#features" className="hover:text-white transition-colors">Features</a>
          <a href="#showcase" className="hover:text-white transition-colors">Showcase</a>
          <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/login" className="text-sm text-white/80 hover:text-white" data-testid="nav-login-link">Log in</Link>
          <Link
            to="/register"
            className="btn-primary px-5 py-2.5 text-xs"
            data-testid="nav-get-started-link"
          >
            Get started
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="relative overflow-hidden border-b border-white/10">
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-60" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/60 to-black" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-28 md:pt-32 md:pb-40">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#38BDF8] font-bold fade-up" style={{ animationDelay: "0.05s" }}>
              v2026 · Coaching OS for pickleball
            </div>
            <h1
              className="font-display text-5xl sm:text-7xl md:text-8xl font-black uppercase tracking-tighter leading-[0.9] mt-6 fade-up"
              style={{ animationDelay: "0.15s" }}
            >
              Run your<br />
              <span className="text-[#38BDF8]">court-side</span><br />
              practice<br />
              like a pro.
            </h1>
            <p className="text-base md:text-lg text-[#A3A3A3] max-w-xl mt-8 leading-relaxed fade-up" style={{ animationDelay: "0.3s" }}>
              PBCOACH.APP is the all-in-one command center for certified coaches. Schedule clinics, onboard students,
              track progress with AI-driven insights, and turn every lesson into measurable improvement.
            </p>
            <div className="flex flex-wrap gap-3 mt-10 fade-up" style={{ animationDelay: "0.45s" }}>
              <Link to="/register" className="btn-primary px-8 py-4 text-sm inline-flex items-center gap-2" data-testid="hero-cta-coach">
                Start coaching free <ArrowUpRight size={16} />
              </Link>
              <Link to="/register?role=student" className="btn-secondary px-8 py-4 text-sm inline-flex items-center gap-2" data-testid="hero-cta-student">
                I'm a student
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-8 mt-12 text-xs text-[#A3A3A3] uppercase tracking-widest fade-up" style={{ animationDelay: "0.6s" }}>
              <div><span className="text-white font-bold text-2xl font-display">12k+</span><br />drills planned</div>
              <div><span className="text-white font-bold text-2xl font-display">98%</span><br />coaches retained</div>
              <div><span className="text-white font-bold text-2xl font-display">3.2h</span><br />saved weekly</div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-4 hidden lg:block relative">
            <div className="absolute -right-10 top-6 rotate-2">
              <img src={ACTION[0]} alt="" className="w-full border border-white/10" />
            </div>
            <div className="absolute -right-4 top-56 -rotate-3">
              <div className="bg-[#141414] border border-white/10 p-5 w-72">
                <div className="text-[10px] tracking-widest uppercase text-[#38BDF8] font-bold">Today · 4:30 PM</div>
                <div className="font-display text-xl font-bold uppercase mt-1">3rd-Shot Drop Lab</div>
                <div className="text-xs text-[#A3A3A3] mt-2">Alex R. · 3.5 DUPR · Court 2</div>
                <div className="mt-4 h-1 bg-white/10">
                  <div className="h-full bg-[#38BDF8]" style={{ width: "64%" }} />
                </div>
                <div className="mt-2 text-[10px] tracking-widest uppercase text-[#A3A3A3]">Consistency · 64%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Kinetic texture */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none">
        <div className="marquee-track whitespace-nowrap flex gap-16 py-3 border-t border-white/10 bg-black/30">
          {Array(6).fill(0).map((_, i) => (
            <span key={i} className="font-display uppercase font-black text-5xl outline-text">
              Dink · Drive · Drop · Reset · Stack · Erne · Poach · Crash ·
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturesBento() {
  const items = [
    { icon: Users2, title: "Student CRM", desc: "Rich profiles with DUPR ratings, goals, notes, and milestone timelines." },
    { icon: CalendarClock, title: "Smart Scheduling", desc: "Drop available slots, let students self-book, auto-confirm via email." },
    { icon: Sparkles, title: "AI Lesson Plans", desc: "Our AI personalizes drills, warm-ups, and game scenarios per student." },
    { icon: Trophy, title: "Progress Radar", desc: "Six-skill visual tracking — watch improvement lesson after lesson." },
    { icon: MessagesSquare, title: "Direct Messaging", desc: "Closed-loop comms between coach and student — feedback in context." },
    { icon: LineChart, title: "Coaching Analytics", desc: "See retention, revenue, and effectiveness per student and per month." },
  ];

  return (
    <section id="features" className="border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32">
        <div className="grid grid-cols-12 gap-8 items-end mb-12">
          <div className="col-span-12 lg:col-span-6">
            <div className="text-[11px] tracking-[0.35em] uppercase text-[#38BDF8] font-bold">Feature Stack</div>
            <h2 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter mt-3">
              Every tool a<br />serious coach needs.
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-6 lg:text-right">
            <p className="text-[#A3A3A3] text-base max-w-md lg:ml-auto">
              One logged-in space replaces your spreadsheets, group chats, calendar apps, and handwritten drill cards.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {items.map((it, i) => {
            const wide = i === 0 || i === 3;
            const I = it.icon;
            return (
              <div
                key={it.title}
                className={`card-surface p-8 ${wide ? "md:col-span-6" : "md:col-span-4"}`}
                data-testid={`feature-card-${i}`}
              >
                <div className="w-10 h-10 border border-[#38BDF8]/50 text-[#38BDF8] grid place-items-center mb-6">
                  <I size={18} aria-hidden="true" />
                </div>
                <div className="font-display uppercase font-black text-2xl tracking-tight">{it.title}</div>
                <p className="text-sm text-[#A3A3A3] mt-3 leading-relaxed">{it.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Showcase() {
  return (
    <section id="showcase" className="border-b border-white/10 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-32 grid grid-cols-12 gap-8 items-center">
        <div className="col-span-12 lg:col-span-5">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#38BDF8] font-bold">The console</div>
          <h2 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter mt-3">
            Control room<br />for every session.
          </h2>
          <p className="text-[#A3A3A3] mt-6 leading-relaxed">
            The coach console brings your roster, calendar, drill library, and analytics onto a single dark-mode canvas —
            optimized for quick glances between points.
          </p>
          <ul className="mt-8 space-y-3 text-sm">
            {["Drag-and-drop clinic scheduling", "Auto-email reminders 24h before sessions", "Per-student progress radar", "AI lesson plans in < 5 seconds"].map((f) => (
              <li key={f} className="flex items-center gap-3 text-white/90">
                <span className="w-2 h-2 bg-[#38BDF8]" /> {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-12 lg:col-span-7 relative">
          <div className="relative border border-white/10 bg-[#141414] p-4">
            <img src={DASHBOARD_IMG} alt="Coach dashboard preview" className="w-full" />
          </div>
          <img src={ABSTRACT} alt="" className="absolute -bottom-10 -left-10 w-40 hidden md:block opacity-80" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  return (
    <section className="border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-12 gap-4">
          {ACTION.map((src, i) => (
            <div key={i} className={`col-span-6 md:col-span-3 border border-white/10 overflow-hidden`}>
              <img src={src} alt="Pickleball action" className="w-full h-48 object-cover" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section id="pricing" className="border-b border-white/10 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 text-center">
        <div className="text-[11px] tracking-[0.35em] uppercase text-[#38BDF8] font-bold">Free to start</div>
        <h2 className="font-display text-5xl md:text-8xl font-black uppercase tracking-tighter mt-3">
          Build a better<br />practice today.
        </h2>
        <p className="text-[#A3A3A3] mt-6 max-w-lg mx-auto">
          Sign up as a coach, invite your roster, and send your first AI-generated lesson plan in under 5 minutes.
        </p>
        <div className="mt-10 flex justify-center gap-3 flex-wrap">
          <Link to="/register" className="btn-primary px-10 py-4 text-sm" data-testid="cta-register-link">
            Claim your workspace
          </Link>
          <Link to="/login" className="btn-secondary px-10 py-4 text-sm" data-testid="cta-login-link">
            Log in
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-10">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#A3A3A3]">
        <div>© 2026 PBCOACH.APP · Built for certified coaches.</div>
        <div className="uppercase tracking-[0.3em]">play · track · improve</div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="landing-page">
      <Header />
      <Hero />
      <FeaturesBento />
      <Showcase />
      <Gallery />
      <CTA />
      <Footer />
    </div>
  );
}
